// Add this at the top of the file for TypeScript compatibility
declare global {
  interface Window {
    // bluetoothSerial?: {
    //   write: (data: string, success: () => void, error: (err: any) => void) => void;
    // };
    bluetoothSerial?: {
      list: (success: (devices: any[]) => void, error: (err: any) => void) => void;
      discoverUnpaired: (success: (devices: any[]) => void, error: (err: any) => void) => void;
      connect: (address: string, success: () => void, error: (err: any) => void) => void;
      disconnect: (success: () => void, error: (err: any) => void) => void;
      write: (data: string, success: () => void, error: (err: any) => void) => void;
      isConnected: (success: () => void, error: () => void) => void;
      isEnabled: (success: () => void, error: () => void) => void;
      enable: (success: () => void, error: (err: any) => void) => void;
      subscribe: (delimiter: string, success: (data: string) => void, error: (err: any) => void) => void;
      unsubscribe: (success: () => void, error: (err: any) => void) => void;
      clear: (success: () => void, error: (err: any) => void) => void;
    };
  }
}
// Kohineer print logic for 2-inch and 3-inch printers
// Accepts invoice details, print size, and company image
import { getApiBaseUrl } from '@/config';

// Robust function to convert numbers to words (handles decimals and large numbers)
function numberToWords(num) {
  if (num === 0) return 'zero';
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
  const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  const scales = ['', 'thousand', 'lakh', 'crore'];
  function convertLessThanOneThousand(n) {
    if (n === 0) return '';
    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) {
      return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
    }
    if (n < 1000) {
      return ones[Math.floor(n / 100)] + ' hundred' + (n % 100 !== 0 ? ' and ' + convertLessThanOneThousand(n % 100) : '');
    }
  }
  function convertIndianNumber(n) {
    if (n === 0) return 'zero';
    const numStr = n.toString();
    const parts = [];
    if (numStr.length > 7) {
      const crorePart = parseInt(numStr.slice(0, -7));
      if (crorePart > 0) {
        parts.push(convertLessThanOneThousand(crorePart) + ' crore');
      }
    }
    if (numStr.length > 5) {
      const lakhPart = parseInt(numStr.slice(-7, -2));
      if (lakhPart > 0) {
        parts.push(convertLessThanOneThousand(lakhPart) + ' lakh');
      }
    }
    const remainingPart = parseInt(numStr.slice(-5));
    if (remainingPart > 0) {
      if (remainingPart < 1000) {
        parts.push(convertLessThanOneThousand(remainingPart));
      } else {
        const thousandPart = Math.floor(remainingPart / 1000);
        const restPart = remainingPart % 1000;
        parts.push(convertLessThanOneThousand(thousandPart) + ' thousand');
        if (restPart > 0) {
          parts.push(convertLessThanOneThousand(restPart));
        }
      }
    }
    return parts.join(' ');
  }
  const numStr = num.toString();
  if (numStr.includes('.')) {
    const [wholePart, decimalPart] = numStr.split('.');
    const wholeWords = convertIndianNumber(parseInt(wholePart));
    const decimalWords = convertIndianNumber(parseInt(decimalPart));
    return wholeWords + ' rupees and ' + decimalWords + ' paise only';
  }
  return convertIndianNumber(Math.floor(num)) + ' rupees only';
}

// Helper function to truncate item names for 2mm printing
function truncateItemName(name: string, maxLength: number): string {
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength - 3) + '...';
}

// Helper function to parse payments from JSON string
function parsePayments(paymentsJson: string): Array<{amount: number, PaymentMode: string, Notes: string}> {
  try {
    if (!paymentsJson) return [];
    const payments = JSON.parse(paymentsJson);
    if (!Array.isArray(payments)) return [];
    
    return payments.map(payment => ({
      amount: Number(payment.amount) || 0,
      PaymentMode: payment.PaymentMode || '',
      Notes: payment.Notes || ''
    })).filter(payment => payment.amount > 0);
  } catch (error) {
    console.error('Error parsing payments JSON:', error);
    return [];
  }
}

/** GST group for GST Calculation table - groups products by GST % and sums tax amounts. */
interface GstGroup {
  gstPercent: number;
  sgst: number;
  cgst: number;
  igst: number;
  cess: number;
}

/** Build GST groups from invoice items: group by GST %, sum SGST/CGST/IGST/CESS per group. */
function buildGstGroups(items: any[], isInterstate: boolean): GstGroup[] {
  if (!Array.isArray(items) || items.length === 0) return [];
  const map = new Map<number, GstGroup>();
  for (const item of items) {
    const gstP = isInterstate
      ? Number(item.igstper ?? item.igstpercent ?? item.IgstPer ?? item.IGSTper) || 0
      : (Number(item.cgstper ?? item.cgstpercent ?? item.CGstPer ?? item.CGSTper) || 0) + (Number(item.sgstper ?? item.sgstpercent ?? item.SgstPer ?? item.SGSTper) || 0);
    const existing = map.get(gstP);
    const sgst = Number(item.SGST) || 0;
    const cgst = Number(item.CGST) || 0;
    const igst = Number(item.IGST) || 0;
    const cess = Number(item.Cess ?? item.cess) || 0;
    if (existing) {
      existing.sgst += sgst;
      existing.cgst += cgst;
      existing.igst += igst;
      existing.cess += cess;
    } else {
      map.set(gstP, { gstPercent: gstP, sgst, cgst, igst, cess });
    }
  }
  return Array.from(map.values())
    .filter(g => g.gstPercent > 0)
    .sort((a, b) => a.gstPercent - b.gstPercent);
}

/** Whether any product in the invoice has CESS (show CESS column if true). */
function hasAnyCess(items: any[]): boolean {
  if (!Array.isArray(items)) return false;
  return items.some(item => (Number(item.Cess ?? item.cess) || 0) > 0);
}

// Helper function to fetch company logo
async function fetchCompanyLogo(invoiceId: string): Promise<string | null> {
  try {
    const baseUrl = getApiBaseUrl();
    const token = localStorage.getItem('token') || '';
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    
    const response = await fetch(`${baseUrl}/api/Invoices/GetCompanyLogo?InvoiceID=${invoiceId}`, {
      headers: {
        'Authorization': authToken,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      console.log('Company logo API failed:', response.status);
      return null;
    }
    
    const data = await response.json();
    console.log('Company logo API response:', data);
    
    if (data.status === 0 && data.data && data.data.length > 0 && data.data[0].LogoPath) {
      return data.data[0].LogoPath;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching company logo:', error);
    return null;
  }
}

export async function printKohineer2mm({ invoiceDetails, companyImage }) {
  // UPDATED VERSION 2024-12-19 - Fixed formatting for api/InvoiceFullDetails
  console.log('=== printKohineer2mm FUNCTION CALLED ===');
  console.log('Invoice Details:', JSON.stringify(invoiceDetails, null, 2));
  
  let printContent = '';
  
  // Calculate total width for 2mm printer (32 characters)
  const totalWidth = 32;
  
  // Get first data item for company and customer details (all rows have same company/customer info)
  const firstItem = invoiceDetails.data && invoiceDetails.data[0] ? invoiceDetails.data[0] : {};
  console.log('First item data for 2mm:', JSON.stringify(firstItem, null, 2));
  
  // Display-only: for Credit Note, show all amounts as positive
  const billHeader2mm = firstItem.Header || firstItem.header || invoiceDetails.Header || invoiceDetails.header || '';
  const isCreditNote2mm = String(billHeader2mm).trim() === 'Credit Note';
  const disp2mm = (v: number) => (isCreditNote2mm ? Math.abs(v) : v);
  
  // Check if company is GST registered (similar to 3mm logic)
  const isGSTRegistered = firstItem.GST === 1 || firstItem.GSTN || firstItem.GSTNo;
  // isIGST: 0 = intrastate (use SGST+CGST), 1 = interstate (use IGST only). Fallback to CustState !== CmpState.
  const isIGST = firstItem.isIGST === 1 || firstItem.isIGST === true || invoiceDetails.isIGST === 1 || invoiceDetails.isIGST === true
    || !!(firstItem.CustState && firstItem.CmpState && firstItem.CustState !== firstItem.CmpState);
  console.log('=== 2MM GST REGISTRATION CHECK ===');
  console.log('Company GST Status:', isGSTRegistered ? 'GST-REGISTERED' : 'NON-GST');
  console.log('Will show tax:', isGSTRegistered ? 'YES' : 'NO');
  console.log('isIGST:', isIGST, '(0=sgst+cgst, 1=igst only)');
  
  // 2mm header: same structure as 3mm (company details, bill header, bill details, customer details)
  const centerText2mm = (text: string, width: number = totalWidth) => {
    const s = String(text || '').trim().slice(0, width);
    const pad = Math.floor((width - s.length) / 2);
    return ' '.repeat(Math.max(0, pad)) + s;
  };
  // Company name from API InvoiceFullDetails "Name" field (print and preview)
  const companyName = (firstItem.Name != null && String(firstItem.Name).trim() !== '') ? String(firstItem.Name).trim() : 'COMPANY NAME';
  const secondline2mm = (firstItem.secondline != null && String(firstItem.secondline).trim() !== '') ? String(firstItem.secondline).trim() : (firstItem.SecondLine != null && String(firstItem.SecondLine).trim() !== '') ? String(firstItem.SecondLine).trim() : '';
  const address1 = firstItem.Address1 || '';
  const address2 = firstItem.Address2 || '';
  const address3 = firstItem.Address3 || '';
  const city = firstItem.City || '';
  const state = firstItem.CmpState || '';
  const pincode = firstItem.PinZip || '';
  const phone = firstItem.Mobile || '';
  // Company block (like 3mm)
  printContent += centerText2mm(companyName) + '\n';
  if (secondline2mm) printContent += centerText2mm(secondline2mm) + '\n';
  if (address1) printContent += centerText2mm(address1) + '\n';
  if (address2) printContent += centerText2mm(address2) + '\n';
  if (address3) printContent += centerText2mm(address3) + '\n';
  const cityStatePincode = [city, state, pincode].filter(Boolean).join(', ');
  if (cityStatePincode) printContent += centerText2mm(cityStatePincode) + '\n';
  if (phone) printContent += centerText2mm(`Phone No: ${phone}`) + '\n';
  printContent += '-'.repeat(totalWidth) + '\n';
  // Bill header (like 3mm)
  printContent += centerText2mm(billHeader2mm || 'BILL OF SUPPLY') + '\n';
  printContent += '-'.repeat(totalWidth) + '\n';
  // Bill details (like 3mm)
  let billDate = '';
  let billTime = '';
  if (firstItem.CreatedOn) {
    const d = new Date(firstItem.CreatedOn);
    billDate = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    billTime = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
  } else {
    const now = new Date();
    billDate = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`;
    billTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
  }
  // Invoice prefix from API - show only when present
  const invoicePrefix2mm = (firstItem.InvoicePrefix != null && String(firstItem.InvoicePrefix).trim() !== '') ? String(firstItem.InvoicePrefix).trim() : '';
  const billNo = invoicePrefix2mm ? `${invoicePrefix2mm}${firstItem.InvSeqNo ?? firstItem.BillNo ?? '0000'}` : (firstItem.InvSeqNo || firstItem.BillNo || '0000');
  printContent += `Bill No   : ${billNo}${' '.repeat(Math.max(0, totalWidth - (`Bill No   : ${billNo}`).length))}\n`;
  printContent += `Date      : ${billDate}${' '.repeat(Math.max(0, totalWidth - (`Date      : ${billDate}`).length))}\n`;
  printContent += `Time      : ${billTime}${' '.repeat(Math.max(0, totalWidth - (`Time      : ${billTime}`).length))}\n`;
  printContent += '-'.repeat(totalWidth) + '\n';
  // Customer details (like 3mm)
  printContent += centerText2mm('Customer Details') + '\n';
  const customerName = firstItem.FName || 'UNKNOWN';
  printContent += customerName + '\n';
  if (firstItem.Mobile1 && String(firstItem.Mobile1).trim()) {
    printContent += `Phone: ${firstItem.Mobile1}\n`;
  }
  printContent += '-'.repeat(totalWidth) + '\n';
  
  // Table header with adjusted column widths to prevent breaking
  const itemWidth = 11;
  const qtyWidth = 5;
  const rateWidth = 6;
  const amtWidth = 8;
  
  // Helper: split value into lines that fit within column width (wrap to next line under same column)
  const split2mm = (value: any, maxWidth: number): string[] => {
    const str = value === undefined || value === null ? '' : String(value);
    if (str.length <= maxWidth) return [str];
    if (str.includes('.')) {
      const [whole, dec] = str.split('.');
      if (whole.length + 1 <= maxWidth) return [whole + '.', dec];
      const mid = Math.floor(maxWidth / 2);
      return [str.slice(0, mid), str.slice(mid)];
    }
    const mid = Math.floor(maxWidth / 2);
    return [str.slice(0, mid), str.slice(mid)];
  };
  
  // Header line with exact spacing
  printContent += 'Item'.padEnd(itemWidth) + 
                 'Qty'.padStart(qtyWidth) + ' ' +
                 'Rate'.padStart(rateWidth) + ' ' +
                 'Amt'.padStart(amtWidth) + '\n';
  
  // Product lines - use actual API data; Qty, Rate, Amt wrap to next line if exceeding column width
  let subtotal = 0;
  let totalTax = 0;
  let totalDiscount = 0;
  
  console.log('Processing 2mm products, data array length:', invoiceDetails.data?.length || 0);
  
  (invoiceDetails.data || []).forEach((item, idx) => {
    console.log(`Processing 2mm item ${idx + 1}:`, JSON.stringify(item, null, 2));
    
    let itemName = String(item.Title || '');
    if (itemName.length > itemWidth) itemName = itemName.slice(0, itemWidth - 3) + '...';
    
    const qty = parseFloat(item.Quantity ?? item.Qty ?? '0') || 0;
    const rate = (typeof item.Rate !== 'undefined' && item.Rate !== null) ? Number(item.Rate) : (typeof item.UnitCost !== 'undefined' && item.UnitCost !== null) ? Number(item.UnitCost) : (typeof item.MRP !== 'undefined' && item.MRP !== null ? Number(item.MRP) : 0);
    const amount = (typeof item.TotalCost !== 'undefined' && item.TotalCost !== null) ? Number(item.TotalCost) : (qty * rate);
    
    const sgst = parseFloat(item.SGST) || 0;
    const cgst = parseFloat(item.CGST) || 0;
    const igst = parseFloat(item.IGST) || 0;
    const itemTax = isIGST ? igst : (sgst + cgst);
    
    // Check multiple discount field names and calculate discount from rate vs amount if needed
    let itemDiscount = parseFloat(item.Discount) || parseFloat(item.discount) || parseFloat(item.Disc) || parseFloat(item.disc) || 0;
    
    // Also check for percentage discount fields
    const discountPercent = parseFloat(item.DiscountPercent) || parseFloat(item.discountPercent) || parseFloat(item.DiscPercent) || parseFloat(item.discPercent) || 0;
    
    // If percentage discount exists, calculate the amount
    if (discountPercent > 0 && rate > 0) {
      const discountAmount = (rate * qty * discountPercent) / 100;
      if (discountAmount > itemDiscount) {
        itemDiscount = discountAmount;
        console.log(`Calculated discount from percentage ${discountPercent}%: ${discountAmount}`);
      }
    }
    
    // If no discount found in fields, calculate it from rate vs amount difference
    if (itemDiscount === 0 && rate > 0 && amount > 0) {
      const expectedAmount = qty * rate;
      if (expectedAmount > amount) {
        itemDiscount = expectedAmount - amount;
        console.log(`Calculated discount from rate difference: ${itemDiscount}`);
      }
    }
    
    // Calculate subtotal excluding GST for GST-registered companies
    let itemSubtotal = amount;
    if (isGSTRegistered && itemTax > 0) {
      itemSubtotal = amount - itemTax;
    }
    
    console.log(`2mm Item ${idx + 1} calculated values:`, { 
      itemName, 
      qty, 
      rate, 
      amount, 
      itemTax, 
      itemSubtotal,
      itemDiscount,
      discountPercent,
      expectedAmount: qty * rate,
      rateAmountDifference: (qty * rate) - amount,
      discountFields: {
        Discount: item.Discount,
        discount: item.discount,
        Disc: item.Disc,
        disc: item.disc,
        DiscountPercent: item.DiscountPercent,
        discountPercent: item.discountPercent,
        DiscPercent: item.DiscPercent,
        discPercent: item.discPercent
      }
    });
    
    // Split Item, Qty, Rate, Amt by column width so long values wrap under same column
    const itemLines = split2mm(itemName, itemWidth);
    const qtyStr = disp2mm(qty).toString();
    const rateStr = disp2mm(rate).toFixed(2);
    const amtStr = Math.round(disp2mm(amount)).toFixed(2);
    const qtyLines = split2mm(qtyStr, qtyWidth);
    const rateLines = split2mm(rateStr, rateWidth);
    const amtLines = split2mm(amtStr, amtWidth);
    const maxLines = Math.max(1, itemLines.length, qtyLines.length, rateLines.length, amtLines.length);
    for (let i = 0; i < maxLines; i++) {
      printContent += (itemLines[i] || '').padEnd(itemWidth) +
        (qtyLines[i] || '').padStart(qtyWidth) + ' ' +
        (rateLines[i] || '').padStart(rateWidth) + ' ' +
        (amtLines[i] || '').padStart(amtWidth) + '\n';
    }
    
    subtotal += itemSubtotal;
    totalTax += itemTax;
    totalDiscount += itemDiscount;
    
    console.log(`Item ${idx + 1} running totals:`, {
      runningSubtotal: subtotal,
      runningTotalTax: totalTax,
      runningTotalDiscount: totalDiscount
    });
  });
  
  console.log('2mm Totals calculated:', { subtotal, totalTax, totalDiscount });
  console.log('=== 2MM DISCOUNT DEBUG ===');
  console.log('Total Discount Value:', totalDiscount);
  console.log('Will show discount line:', totalDiscount > 0 ? 'YES' : 'NO');
  console.log('=== END DISCOUNT DEBUG ===');
  
  // Separator line
  printContent += '-'.repeat(totalWidth) + '\n';
  
  // Totals section with proper alignment
  const labelWidth = 12;
  const valueWidth = totalWidth - labelWidth;
  
  printContent += 'Subtotal:'.padEnd(labelWidth) + disp2mm(subtotal).toFixed(2).padStart(valueWidth) + '\n';
  
  // Show discount below subtotal (if any discount exists)
  if (Math.abs(totalDiscount) > 0) {
    printContent += 'Discount:'.padEnd(labelWidth) + disp2mm(totalDiscount).toFixed(2).padStart(valueWidth) + '\n';
    console.log('Added discount line to print content');
  } else {
    console.log('No discount to show (totalDiscount <= 0)');
  }
  
  // Show tax only for GST-registered companies
  if (isGSTRegistered && Math.abs(totalTax) > 0) {
    printContent += 'Tax:'.padEnd(labelWidth) + disp2mm(totalTax).toFixed(2).padStart(valueWidth) + '\n';
  }
  
  // Calculate total based on GST registration
  const total = isGSTRegistered ? subtotal + totalTax : subtotal;
  console.log('=== 2MM TOTAL CALCULATION ===');
  console.log('GST Registered:', isGSTRegistered);
  console.log('Subtotal:', subtotal);
  console.log('Total Tax:', totalTax);
  console.log('Final Total:', total);
  console.log('=== END TOTAL CALCULATION ===');
  
  printContent += 'Total:'.padEnd(labelWidth) + Math.round(disp2mm(total)).toFixed(2).padStart(valueWidth) + '\n';
  
  // Payment information - use new Payments JSON array
  const payments = parsePayments(firstItem.Payments);
  let hasPaymentShown = false;
  
  if (payments.length > 0) {
    payments.forEach(payment => {
      const paymentLabel = payment.PaymentMode.charAt(0).toUpperCase() + payment.PaymentMode.slice(1).toLowerCase();
      printContent += paymentLabel.padEnd(labelWidth) + Math.round(disp2mm(payment.amount)).toFixed(2).padStart(valueWidth) + '\n';
      hasPaymentShown = true;
    });
  } else {
    // Fallback to old payment fields if Payments JSON is not available (display as positive for Credit Note)
    if (firstItem.PaidbyCash && Math.abs(Number(firstItem.PaidbyCash)) > 0) {
      printContent += 'Cash:'.padEnd(labelWidth) + disp2mm(Number(firstItem.PaidbyCash)).toFixed(2).padStart(valueWidth) + '\n';
      hasPaymentShown = true;
    }
    if (firstItem.walletamount && Math.abs(Number(firstItem.walletamount)) > 0) {
      printContent += 'UPI:'.padEnd(labelWidth) + disp2mm(Number(firstItem.walletamount)).toFixed(2).padStart(valueWidth) + '\n';
      hasPaymentShown = true;
    }
    if (firstItem.PaidbyCard && Math.abs(Number(firstItem.PaidbyCard)) > 0) {
      printContent += 'Card:'.padEnd(labelWidth) + disp2mm(Number(firstItem.PaidbyCard)).toFixed(2).padStart(valueWidth) + '\n';
      hasPaymentShown = true;
    }
    if (firstItem.Couponamount && Math.abs(Number(firstItem.Couponamount)) > 0) {
      printContent += 'Coupon:'.padEnd(labelWidth) + disp2mm(Number(firstItem.Couponamount)).toFixed(2).padStart(valueWidth) + '\n';
      hasPaymentShown = true;
    }
    if (firstItem.CreditNoteamount && Math.abs(Number(firstItem.CreditNoteamount)) > 0) {
      printContent += 'Credit:'.padEnd(labelWidth) + disp2mm(Number(firstItem.CreditNoteamount)).toFixed(2).padStart(valueWidth) + '\n';
      hasPaymentShown = true;
    }
    if (firstItem.Chequeamount && Math.abs(Number(firstItem.Chequeamount)) > 0) {
      printContent += 'Cheque:'.padEnd(labelWidth) + disp2mm(Number(firstItem.Chequeamount)).toFixed(2).padStart(valueWidth) + '\n';
      hasPaymentShown = true;
    }
    if (firstItem.PaidbyOthers && Math.abs(Number(firstItem.PaidbyOthers)) > 0) {
      printContent += 'Others:'.padEnd(labelWidth) + disp2mm(Number(firstItem.PaidbyOthers)).toFixed(2).padStart(valueWidth) + '\n';
      hasPaymentShown = true;
    }
    if (firstItem.PaidbyPoints && Math.abs(Number(firstItem.PaidbyPoints)) > 0) {
      printContent += 'Points:'.padEnd(labelWidth) + disp2mm(Number(firstItem.PaidbyPoints)).toFixed(2).padStart(valueWidth) + '\n';
      hasPaymentShown = true;
    }
  }
  
  // Show Due Amount if there is any
  if (firstItem.DueAmount && Math.abs(Number(firstItem.DueAmount)) > 0) {
    printContent += 'Due:'.padEnd(labelWidth) + disp2mm(Number(firstItem.DueAmount)).toFixed(2).padStart(valueWidth) + '\n';
  }
  
  // Terms and Conditions from API - show only when non-empty (no hardcoded text)
  const terms2mm = (firstItem.InvoiceTermsConditions != null && String(firstItem.InvoiceTermsConditions).trim() !== '') ? String(firstItem.InvoiceTermsConditions).trim() : '';
  if (terms2mm) {
    printContent += '-'.repeat(totalWidth) + '\n';
    printContent += centerText2mm('TERMS AND CONDITIONS') + '\n';
    const termsLines2mm = terms2mm.split(/\r?\n/);
    termsLines2mm.forEach((line) => {
      const trimmed = line.trim();
      if (trimmed) printContent += centerText2mm(trimmed) + '\n';
    });
  }
  
  // Separator line
  printContent += '-'.repeat(totalWidth) + '\n';
  
  // Thank you message (centered)
  const thankYou = 'Thank you! Visit again';
  const thankYouPadding = Math.floor((totalWidth - thankYou.length) / 2);
  printContent += ' '.repeat(Math.max(0, thankYouPadding)) + thankYou + '\n';
  
  console.log('=== FINAL 2MM PRINT CONTENT ===');
  console.log(printContent);
  console.log('=== END 2MM PRINT CONTENT ===');
  
  // Import unified Bluetooth interface and thermal logo printer
  const { printWithBluetooth, isBluetoothSupported } = await import('./unifiedBluetoothPrinter');
  const { Capacitor } = await import('@capacitor/core');
  const isAndroid = Capacitor.getPlatform() === 'android';
  
  // Company logo disabled on Android Bluetooth thermal printer (no header logo)
  let logoData: Uint8Array | null = null;
  const invoiceIdForLogo2mm = firstItem.Invoiceid ?? invoiceDetails.Invoiceid ?? invoiceDetails.InvoiceID;
  if (false) { // was: isAndroid && invoiceIdForLogo2mm != null - logo removed for Android
    const invoiceId = String(invoiceIdForLogo2mm);
    const logoUrl = await fetchCompanyLogo(invoiceId);
    if (logoUrl) {
      const baseUrl = getApiBaseUrl();
      const token = localStorage.getItem('token') || '';
      const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      const fullLogoUrl = logoUrl.startsWith('http') ? logoUrl : `${baseUrl.replace(/\/$/, '')}${logoUrl.startsWith('/') ? '' : '/'}${logoUrl}`;
      const { imageUrlToEscPosBitmap } = await import('./thermalLogoPrinter');
      logoData = await imageUrlToEscPosBitmap(fullLogoUrl, 384, authToken);
      if (logoData) {
        console.log('[printKohineer2mm] Company logo converted for thermal print');
      }
    }
  }
  
  // Check if Bluetooth is supported on this platform
  const supported = await isBluetoothSupported();
  if (supported) {
    console.log('Sending 2mm to Bluetooth printer via unified interface...');
    try {
      const result = await printWithBluetooth(printContent, logoData);
      if (result.success) {
        console.log('2mm Bluetooth print successful!');
        return { success: true, message: result.message };
      } else {
        throw new Error(result.message || 'Print failed');
      }
    } catch (error: any) {
      console.error('2mm Bluetooth print failed:', error);
      throw error;
    }
  } else {
    console.log('2mm Bluetooth not available, logging print content');
    console.log('2mm Print content length:', printContent.length);
    return { success: true, message: 'Bluetooth not available - preview mode' };
  }
}

/** Wrap text into lines of max length (break at spaces when possible). */
function wrapTextToLines(text: string, maxLen: number): string[] {
  const t = String(text || '').trim();
  if (!t) return [''];
  if (t.length <= maxLen) return [t];
  const lines: string[] = [];
  let remaining = t;
  while (remaining.length > 0) {
    if (remaining.length <= maxLen) {
      lines.push(remaining);
      break;
    }
    const chunk = remaining.slice(0, maxLen + 1);
    const lastSpace = chunk.lastIndexOf(' ');
    const breakAt = lastSpace > 0 ? lastSpace : maxLen;
    lines.push(remaining.slice(0, breakAt).trim());
    remaining = remaining.slice(breakAt).trim();
  }
  return lines.length ? lines : [''];
}

export async function printKohineer({ invoiceDetails, size, companyImage, companyInfo, showSerialInPrint, serialPrintTemplate }: { invoiceDetails: any; size: string; companyImage: any; companyInfo: any; showSerialInPrint?: boolean; serialPrintTemplate?: string }) {
  // FULL 3MM INVOICE PRINTER - DYNAMIC HSN COLUMN BASED ON GST REGISTRATION v4.2
  // companyInfo is fetched fresh from api/Companies when print is checked
  // serialPrintTemplate: from Settings "Serial Print"; replace <SerialNo> with actual serial and show full text (no hardcoded S/N)
  const timestamp = new Date().getTime();
  const functionId = Math.random().toString(36).substring(7);
  console.log(`=== printKohineer FUNCTION CALLED [${timestamp}] [ID:${functionId}] [VERSION:4.2] ===`);
  console.log('🔧 SEPARATOR FIX VERSION 3.0 - Using direct string concatenation like 2mm function');
  console.log('Size parameter:', size);
  console.log('typeof window:', typeof window);
  console.log('window.bluetoothSerial available:', typeof window !== 'undefined' && !!window.bluetoothSerial);
  // Log full invoiceDetails in JSON format (like Postman)
  console.log('=== FULL invoiceDetails (JSON) ===');
  console.log(JSON.stringify(invoiceDetails, null, 2));
  console.log('=== END invoiceDetails ===');
  console.log('Invoice Details:', JSON.stringify(invoiceDetails, null, 2));
  
  let printContent = '';
  
  // Calculate total width for 3mm printer (45 characters)
  const totalWidth = 45;
  
  // Helper function to center text
  const centerText = (text: string, width: number = totalWidth): string => {
    const padding = Math.floor((width - text.length) / 2);
    return ' '.repeat(Math.max(0, padding)) + text;
  };
  
  // // Helper function to create separator line
  // const separator = () => {
  //   console.log('🔧 Creating separator for 3mm printer, totalWidth:', totalWidth);
  //   
  //   // Try multiple separator styles - some printers are picky about characters
  //   const separatorOptions = [
  //     '='.repeat(totalWidth),           // equals signs
  //     '*'.repeat(totalWidth),           // asterisks  
  //     '_'.repeat(totalWidth),           // underscores
  //     '-'.repeat(totalWidth),           // dashes (current)
  //     '·'.repeat(totalWidth),           // middle dots
  //     '═'.repeat(totalWidth),           // double line box drawing
  //     '━'.repeat(totalWidth),           // heavy horizontal line
  //   ];
  //   
  //   // Try thermal printer specific approaches
  //   const thermalSpecificSeparators = [
  //     // Method 1: Simple repeated equals
  //     '='.repeat(totalWidth),
  //     
  //     // Method 2: Mixed characters for visibility
  //     '='.repeat(Math.floor(totalWidth/2)) + '='.repeat(Math.ceil(totalWidth/2)),
  //     
  //     // Method 3: Alternating pattern
  //     Array(totalWidth).fill(0).map((_, i) => i % 2 === 0 ? '=' : '=').join(''),
  //     
  //     // Method 4: Thermal printer escape sequence + line
  //     '\x1B\x21\x00' + '='.repeat(totalWidth) + '\x1B\x21\x00', // ESC ! 0 (normal) + line + ESC ! 0
  //     
  //     // Method 5: Bold formatting + line
  //     '\x1B\x45\x01' + '='.repeat(totalWidth) + '\x1B\x45\x00', // ESC E 1 (bold on) + line + ESC E 0 (bold off)
  //   ];
  //   
  //   // For now, let's try the first thermal-specific approach
  //   const chosenSeparator = thermalSpecificSeparators[0]; // simple equals
  //   console.log('🔧 Using separator:', chosenSeparator.substring(0, 10) + '...');
  //   console.log('🔧 Separator length:', chosenSeparator.length);
  //   console.log('🔧 Separator raw:', JSON.stringify(chosenSeparator.substring(0, 10)));
  //   
  //   return chosenSeparator;
  //   // addCenteredLine('------------------------------------------------');
  // };



  // Helper to add content with proper spacing
  const addLine = (text: string) => {
    printContent += text + '\n';
  };

  // Helper function to split long values vertically within column width
  const splitValueForColumn = (value: any, maxWidth: number): string[] => {
    const str = value === undefined || value === null ? '' : String(value);
    if (str.length <= maxWidth) {
      return [str];
    }
    
    // Handle decimal numbers properly
    if (str.includes('.')) {
      const [wholePart, decimalPart] = str.split('.');
      
      // If whole part fits, put decimal point with whole part
      if (wholePart.length + 1 <= maxWidth) {
        const firstLine = wholePart + '.';
        const secondLine = decimalPart;
        return [firstLine, secondLine];
      }
      
      // If whole part is too long, split the whole part
      if (wholePart.length > maxWidth) {
        const midPoint = Math.floor(maxWidth / 2);
        return [wholePart.substring(0, midPoint), wholePart.substring(midPoint) + '.' + decimalPart];
      }
    }
    
    // For non-decimal numbers, split at middle
    const midPoint = Math.floor(maxWidth / 2);
    return [str.substring(0, midPoint), str.substring(midPoint)];
  };

  // Get first data item for company and customer details (all rows have same company/customer info)
  const firstItem = invoiceDetails.data && invoiceDetails.data[0] ? invoiceDetails.data[0] : {};
  console.log('First item data:', JSON.stringify(firstItem, null, 2));
  
  // Get Header from API response (from invoiceDetails or firstItem), fallback to "BILL OF SUPPLY"
  // Check multiple possible locations for Header field - prioritize firstItem since that's where it usually is
  const billHeader = firstItem.Header || 
                     firstItem.header ||
                     invoiceDetails.Header ||
                     invoiceDetails.header ||
                     'BILL OF SUPPLY';
  // Display-only: for Credit Note, show all amounts as positive
  const isCreditNote3mm = String(billHeader || '').trim() === 'Credit Note';
  const disp3mm = (v: number) => (isCreditNote3mm ? Math.abs(v) : v);
  console.log('[printKohineer] billHeader value:', billHeader);
  console.log('[printKohineer] Header search - firstItem.Header:', firstItem.Header);
  console.log('[printKohineer] Header search - firstItem.header:', firstItem.header);
  console.log('[printKohineer] Header search - invoiceDetails.Header:', invoiceDetails.Header);
  console.log('[printKohineer] Header search - invoiceDetails.header:', invoiceDetails.header);
  
  // Company name from API InvoiceFullDetails "Name" field (print and preview)
  const companyName = (firstItem.Name != null && String(firstItem.Name).trim() !== '') ? String(firstItem.Name).trim() : 'COMPANY NAME';
  const secondline3mm = (firstItem.secondline != null && String(firstItem.secondline).trim() !== '') ? String(firstItem.secondline).trim() : (firstItem.SecondLine != null && String(firstItem.SecondLine).trim() !== '') ? String(firstItem.SecondLine).trim() : '';
  const address1 = firstItem.Address1 || '';
  const address2 = firstItem.Address2 || '';
  const address3 = firstItem.Address3 || '';
  const city = firstItem.City || '';
  const state = firstItem.CmpState || '';
  const pincode = firstItem.PinZip || '';
  const phone = firstItem.Mobile || '';
  
  // Determine if company is GST-registered - use same logic as preview and 2mm printer
  const isGSTRegistered = firstItem.GST === 1 || firstItem.GSTN || firstItem.GSTNo;
  console.log('=== GST REGISTRATION CHECK ===');
  console.log('First item GST field value:', firstItem.GST);
  console.log('First item GSTN field value:', firstItem.GSTN);
  console.log('First item GSTNo field value:', firstItem.GSTNo);
  console.log('Is GST Registered:', isGSTRegistered ? 'YES' : 'NO');
  console.log('Will show HSN column:', isGSTRegistered ? 'YES' : 'NO');
  console.log('================================');
  
  // Column helpers for GST thermal (45-char width) - GST column in product table hidden per requirement
  const gstColumnWidths = { hsn: 5, mrp: 7, qty: 5, offer: 10, total: 8 };
  const gstSep = ' ';
  const gstSepAfterMrp = '  ';   // extra gap between MRP and Qty
  const gstSepQtyToOffer = '';    // no space so Qty sits 1 pt nearer to Offer Price
  const gstSepBeforeTotal = '  '; // add an extra space before Total to match PDF spacing

  // Helper function to create table row with vertical splitting (GST column hidden in product table)
  const createTableRow = (values: { hsn: string, mrp: string, qty: string, disc?: string, gst: string, offerPrice: string, total: string }) => {
    console.log(`createTableRow called - isGSTRegistered: ${isGSTRegistered}`);
    
    if (isGSTRegistered) {
      console.log('Using GST-registered layout (HSN, MRP, Qty, OfferPrice, Total) - GST column hidden');
      // GST-registered: HSN, MRP, Qty, OfferPrice, Total (GST column hidden; GST calculation table unchanged)
      const hsnLines = splitValueForColumn(values.hsn, gstColumnWidths.hsn);
      const mrpLines = splitValueForColumn(values.mrp, gstColumnWidths.mrp);
      const qtyLines = splitValueForColumn(values.qty, gstColumnWidths.qty);
      const offerPriceLines = splitValueForColumn(values.offerPrice, gstColumnWidths.offer);
      const totalLines = splitValueForColumn(values.total, gstColumnWidths.total);
      
      const maxLines = Math.max(hsnLines.length, mrpLines.length, qtyLines.length, offerPriceLines.length, totalLines.length);
      
      for (let i = 0; i < maxLines; i++) {
        const line =
          `${(hsnLines[i] || '').padEnd(gstColumnWidths.hsn)}${gstSep}` +
          `${(mrpLines[i] || '').padStart(gstColumnWidths.mrp)}${gstSepAfterMrp}` +
          `${(qtyLines[i] || '').padStart(gstColumnWidths.qty)}${gstSepQtyToOffer}` +
          `${(offerPriceLines[i] || '').padStart(gstColumnWidths.offer)}${gstSepBeforeTotal}` +
          `${(totalLines[i] || '').padStart(gstColumnWidths.total)}`;
        addLine(line);
      }
    } else {
      console.log('Using Non-GST layout (MRP, Qty, Disc, Offer Price, Total) - even fixed widths');
      // Non-GST: five columns with even, fixed widths to avoid overlap
      const mrpWidth = 8;
      const qtyWidth = 6;
      const discWidth = 6;
      const offerPriceWidth = 10;
      const totalWidthCol = 10;
      const mrpLines = splitValueForColumn(values.mrp, mrpWidth);
      const qtyLines = splitValueForColumn(values.qty, qtyWidth);
      const discLines = splitValueForColumn(values.disc || '0.00', discWidth);
      const offerPriceLines = splitValueForColumn(values.offerPrice || '0.00', offerPriceWidth);
      const totalLines = splitValueForColumn(values.total, totalWidthCol);
      
      const maxLines = Math.max(mrpLines.length, qtyLines.length, discLines.length, offerPriceLines.length, totalLines.length);
      
      for (let i = 0; i < maxLines; i++) {
        const line = `${(mrpLines[i] || '').padStart(mrpWidth)} ${(qtyLines[i] || '').padStart(qtyWidth)} ${(discLines[i] || '').padStart(discWidth)} ${(offerPriceLines[i] || '').padStart(offerPriceWidth)} ${(totalLines[i] || '').padStart(totalWidthCol)}`;
        addLine(line);
      }
    }
  };
  
  console.log('Company details being used:', { companyName, address1, address2, address3, city, state, pincode, phone });
  
  // Add company header with proper spacing
  addLine(centerText(companyName));
  if (secondline3mm) addLine(centerText(secondline3mm));
  addLine(centerText(address1));
  addLine(centerText(address2));
  addLine(centerText(address3));
  addLine(centerText(`${city}, ${state}, ${pincode}`));
  addLine(centerText(`Phone No: ${phone}`));
  // Using asterisks - confirmed working character for this thermal printer
  printContent += '-'.repeat(totalWidth) + '\n';
  
  // Bill Header (from API or fallback to "BILL OF SUPPLY")
  addLine(centerText(billHeader));
  printContent += '-'.repeat(totalWidth) + '\n';
  
  // Bill Details - invoice prefix from API, show only when present
  const invoicePrefix3mm = (firstItem.InvoicePrefix != null && String(firstItem.InvoicePrefix).trim() !== '') ? String(firstItem.InvoicePrefix).trim() : '';
  const billNo = invoicePrefix3mm ? `${invoicePrefix3mm}${firstItem.InvSeqNo ?? '0000'}` : (firstItem.InvSeqNo || '0000');
  let billDate = '', billTime = '';
  if (firstItem.CreatedOn) {
    const d = new Date(firstItem.CreatedOn);
    billDate = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    billTime = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
  } else {
    const now = new Date();
    billDate = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`;
    billTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
  }
  
  addLine(`Bill No   : ${billNo}${' '.repeat(Math.max(0, totalWidth - (`Bill No   : ${billNo}`).length))}`);
  addLine(`Date      : ${billDate}${' '.repeat(Math.max(0, totalWidth - (`Date      : ${billDate}`).length))}`);
  addLine(`Time      : ${billTime}${' '.repeat(Math.max(0, totalWidth - (`Time      : ${billTime}`).length))}`);
  printContent += '-'.repeat(totalWidth) + '\n';
  
  // Customer Details Section
  addLine(centerText('Customer Details'));
  const customerName = firstItem.FName || 'UNKNOWN';
  addLine(customerName);
  if (firstItem.Mobile1 && firstItem.Mobile1.trim()) {
    addLine(`Phone: ${firstItem.Mobile1}`);
  }
  printContent += '-'.repeat(totalWidth) + '\n';
  
  // Product Table Header - S.No (3 chars) + space + Item Name (30 chars) = 34 chars
  addLine('S.No Item Name');
  
  // Create header with fixed column widths according to specifications
  // Column widths: HSN(6) + Unit Price/MRP(8) + Qty(5) + GST(6) + Disc(6) + OfferPrice(10) + Total(8) + spaces = 45 chars
  // Determine if it's interstate (IGST) or intrastate (GST): use API isIGST (0=sgst+cgst, 1=igst) or fallback to state comparison
  const isIGST = firstItem.isIGST === 1 || firstItem.isIGST === true || invoiceDetails.isIGST === 1 || invoiceDetails.isIGST === true
    || !!(firstItem.CustState && firstItem.CmpState && firstItem.CustState !== firstItem.CmpState);
  const isInterstate = isIGST;
  const gstColumnHeader = isInterstate ? 'IGST' : 'GST';
  
  // Create header line based on GST registration status (GST column hidden in product table)
  // GST: HSN, MRP, Qty, OfferPrice, Total
  // Non-GST: MRP, Qty, Disc, OfferPrice, Total
  let headerLine;
  let headerLineRow2: string | null = null;
  if (isGSTRegistered) {
    headerLine = `${'HSN'.padEnd(gstColumnWidths.hsn)}${gstSep}${'MRP'.padStart(gstColumnWidths.mrp)}${gstSepAfterMrp}${'Qty'.padStart(gstColumnWidths.qty)}${gstSepQtyToOffer}${'Offer'.padStart(gstColumnWidths.offer)}${gstSepBeforeTotal}${'Total'.padStart(gstColumnWidths.total)}`;
    headerLineRow2 = `${''.padEnd(gstColumnWidths.hsn)}${gstSep}${''.padEnd(gstColumnWidths.mrp)}${gstSepAfterMrp}${''.padEnd(gstColumnWidths.qty)}${gstSepQtyToOffer}${'Price'.padStart(gstColumnWidths.offer)}${gstSepBeforeTotal}${''.padStart(gstColumnWidths.total)}`;
  } else {
    // Non-GST: keep Disc and use even, fixed widths to avoid overlap
    const mrpWidth = 8;
    const qtyWidth = 6;
    const discWidth = 6;
    const offerPriceWidth = 10;
    const totalWidthCol = 10;
    headerLine = `${'MRP'.padStart(mrpWidth)} ${'Qty'.padStart(qtyWidth)} ${'Disc'.padStart(discWidth)} ${'Offer'.padStart(offerPriceWidth)} ${'Total'.padStart(totalWidthCol)}`;
    headerLineRow2 = `${''.padEnd(mrpWidth)} ${''.padEnd(qtyWidth)} ${''.padEnd(discWidth)} ${'Price'.padStart(offerPriceWidth)} ${''.padStart(totalWidthCol)}`;
  }
  addLine(headerLine);
  if (headerLineRow2) addLine(headerLineRow2);
  
  // Product lines
  let totalQty = 0, totalDisc = 0, totalGST = 0, totalTotal = 0, totalOfferPrice = 0;
  
  const itemNameColumnWidth = 30; // chars for item name (S.No + space = 4, then 30 for name)
  const useSerialTemplate = Boolean(serialPrintTemplate && serialPrintTemplate.includes('<SerialNo>'));
  (invoiceDetails.data || []).forEach((item, idx) => {
    let itemName = String(item.Title || 'New Product');
    // Only show serial tag when SerialNumber has a real value (exclude null, empty, "-", "null")
    const rawSerial = item.SerialNumber;
    const serialNum = (rawSerial != null && rawSerial !== '' && String(rawSerial).trim() !== '' && String(rawSerial).trim() !== '-' && String(rawSerial).toLowerCase() !== 'null')
      ? String(rawSerial).trim() : '';
    const serialPart = useSerialTemplate && serialNum ? serialPrintTemplate!.replace('<SerialNo>', serialNum) : '';
    const fullItemText = serialPart ? `${itemName} (${serialPart})` : itemName;
    const itemLines = wrapTextToLines(fullItemText, itemNameColumnWidth);
    itemLines.forEach((line, i) => {
      const padded = line.length > itemNameColumnWidth ? line.substring(0, itemNameColumnWidth - 3) + '...' : line.padEnd(itemNameColumnWidth);
      addLine(i === 0 ? `${String(idx + 1).padStart(3, '0')} ${padded}` : `    ${padded}`);
    });

    const hsn = (item.HSNCode || item.ProductCode || item.HSN || '1000').toString().trim();
    
    // Use different price variables based on GST registration status
    let unitPrice, mrpPrice, offerPrice;
    const qty = Number(item.Quantity || item.Qty || 1);
    const discountValue = Number(item.ldiscount || item.Discount || 0);
    const sgst = Number(item.SGST || 0);
    const cgst = Number(item.CGST || 0);
    const igst = Number(item.IGST || 0);
    const gstTotal = isIGST ? igst : (sgst + cgst);
    // Use TotalCost from API for the total amount, with fallback to calculated value
    const totalVal = (typeof item.TotalCost !== 'undefined' && item.TotalCost !== null) ? Number(item.TotalCost) : 0;
    
    if (isGSTRegistered) {
      // For GST companies: MRP is the Rate/MRP field, OfferPrice is calculated from TotalCost
      mrpPrice = Number(item.Rate || item.MRP || item.unitprice || 100);
      // Calculate offer price dynamically: offerprice = TotalCost / Quantity
      offerPrice = qty > 0 ? totalVal / qty : mrpPrice;
      unitPrice = mrpPrice; // Keep for backward compatibility
    } else {
      // For non-GST companies: Use Rate for MRP column
      mrpPrice = Number(item.Rate || item.MRP || item.unitprice || 100);
      unitPrice = mrpPrice; // Same value for non-GST companies
      offerPrice = qty > 0 ? totalVal / qty : mrpPrice;
    }
    
    // Use vertical splitting for long values to maintain column alignment (display amounts as positive for Credit Note)
    console.log(`Processing item ${idx + 1}: HSN=${hsn}, GST-Registered=${isGSTRegistered}, MRP=${mrpPrice}, OfferPrice=${offerPrice}`);
    createTableRow({
      hsn: hsn,
      mrp: disp3mm(mrpPrice).toFixed(2),
      qty: disp3mm(qty).toFixed(2),
      gst: disp3mm(gstTotal).toFixed(2),
      disc: disp3mm(discountValue).toFixed(2),
      offerPrice: disp3mm(offerPrice).toFixed(2),
      total: Math.round(disp3mm(totalVal)).toFixed(2)
    });
    
    totalQty += qty;
    totalDisc += discountValue;
    totalGST += gstTotal;
    totalTotal += totalVal;
    totalOfferPrice += (offerPrice * qty); // accumulate extended offer price for totals
  });
  
  printContent += '-'.repeat(totalWidth) + '\n';
  
  // Totals section (GST column hidden in product table)
  if (isGSTRegistered) {
    const totQtyLines = splitValueForColumn(disp3mm(totalQty).toFixed(2), gstColumnWidths.qty);
    const totOfferLines = splitValueForColumn(disp3mm(totalOfferPrice).toFixed(2), gstColumnWidths.offer);
    const totTotalLines = splitValueForColumn(Math.round(disp3mm(totalTotal)).toFixed(2), gstColumnWidths.total);
    const totMaxLines = Math.max(1, totQtyLines.length, totOfferLines.length, totTotalLines.length);
    for (let i = 0; i < totMaxLines; i++) {
      const label = i === 0 ? 'Total:' : '';
      const line =
        `${label.padEnd(6)}${gstSep}` +
        `${''.padStart(gstColumnWidths.mrp)}${gstSepAfterMrp}` +
        `${(totQtyLines[i] || '').padStart(gstColumnWidths.qty)}${gstSepQtyToOffer}` +
        `${(totOfferLines[i] || '').padStart(gstColumnWidths.offer)}${gstSepBeforeTotal}` +
        `${(totTotalLines[i] || '').padStart(gstColumnWidths.total)}`;
      addLine(line);
    }
  } else {
    createTableRow({
      hsn: '',
      mrp: 'Total:',
      qty: disp3mm(totalQty).toFixed(2),
      gst: '',
      disc: disp3mm(totalDisc).toFixed(2),
      offerPrice: disp3mm(totalOfferPrice).toFixed(2),
      total: Math.round(disp3mm(totalTotal)).toFixed(2)
    });
  }
  printContent += '-'.repeat(totalWidth) + '\n';
  addLine(''); // one-line gap between Total section and "Received by" (match Windows)
  
  // Payment information - use new Payments JSON array
  const payments = parsePayments(firstItem.Payments);
  let hasPaymentShown = false;
  
  if (payments.length > 0) {
    payments.forEach(payment => {
      const paymentLabel = payment.PaymentMode.charAt(0).toUpperCase() + payment.PaymentMode.slice(1).toLowerCase();
      addLine(`Received by ${paymentLabel}: Rs.${Math.round(disp3mm(payment.amount)).toFixed(2)}`);
      hasPaymentShown = true;
    });
  } else {
    // Fallback to old payment fields if Payments JSON is not available (display as positive for Credit Note)
    if (firstItem.PaidbyCash && Math.abs(Number(firstItem.PaidbyCash)) > 0) {
      addLine(`Received by Cash: Rs.${disp3mm(Number(firstItem.PaidbyCash)).toFixed(2)}`);
      hasPaymentShown = true;
    }
    if (firstItem.walletamount && Math.abs(Number(firstItem.walletamount)) > 0) {
      addLine(`Received by UPI/Wallet: Rs.${disp3mm(Number(firstItem.walletamount)).toFixed(2)}`);
      hasPaymentShown = true;
    }
    if (firstItem.PaidbyCard && Math.abs(Number(firstItem.PaidbyCard)) > 0) {
      addLine(`Received by Card: Rs.${disp3mm(Number(firstItem.PaidbyCard)).toFixed(2)}`);
      hasPaymentShown = true;
    }
    if (firstItem.Couponamount && Math.abs(Number(firstItem.Couponamount)) > 0) {
      addLine(`Received by Coupon: Rs.${disp3mm(Number(firstItem.Couponamount)).toFixed(2)}`);
      hasPaymentShown = true;
    }
    if (firstItem.CreditNoteamount && Math.abs(Number(firstItem.CreditNoteamount)) > 0) {
      addLine(`Received by Credit Note: Rs.${disp3mm(Number(firstItem.CreditNoteamount)).toFixed(2)}`);
      hasPaymentShown = true;
    }
    if (firstItem.Chequeamount && Math.abs(Number(firstItem.Chequeamount)) > 0) {
      addLine(`Received by Cheque: Rs.${disp3mm(Number(firstItem.Chequeamount)).toFixed(2)}`);
      hasPaymentShown = true;
    }
    if (firstItem.PaidbyOthers && Math.abs(Number(firstItem.PaidbyOthers)) > 0) {
      addLine(`Received by Others: Rs.${disp3mm(Number(firstItem.PaidbyOthers)).toFixed(2)}`);
      hasPaymentShown = true;
    }
    if (firstItem.PaidbyPoints && Math.abs(Number(firstItem.PaidbyPoints)) > 0) {
      addLine(`Received by Points: Rs.${disp3mm(Number(firstItem.PaidbyPoints)).toFixed(2)}`);
      hasPaymentShown = true;
    }
  }
  
  // Show Due Amount if there is any 
  if (firstItem.DueAmount && Math.abs(Number(firstItem.DueAmount)) > 0) {
    addLine(`Due Amount: Rs.${disp3mm(Number(firstItem.DueAmount)).toFixed(2)}`);
  }
  
  printContent += '-'.repeat(totalWidth) + '\n';
  
  // Amount in words (display as positive for Credit Note)
  addLine('TOTAL TAX INVOICE AMOUNT IN WORDS');
  const totalAmount = Number(firstItem.InvoicedAmount || totalTotal);
  const amountInWords = numberToWords(Math.round(disp3mm(totalAmount))).toUpperCase();
  
  // Split long amount in words to fit within page width
  const maxLineLength = totalWidth - 4; // Leave 2 spaces margin on each side
  const wordsArray = amountInWords.split(' ');
  let currentLine = '';
  
  wordsArray.forEach(word => {
    if ((currentLine + ' ' + word).length <= maxLineLength) {
      currentLine += (currentLine ? ' ' : '') + word;
    } else {
      if (currentLine) {
        addLine(currentLine);
      }
      currentLine = word;
    }
  });
  
  if (currentLine) {
    addLine(currentLine);
  }
  printContent += '-'.repeat(totalWidth) + '\n';
  
  // GST Calculation Table - Group by GST %, dynamic CESS column - Only for GST-registered companies
  if (isGSTRegistered) {
    addLine(centerText('GST CALCULATION'));
    
    const items3mm = Array.isArray(invoiceDetails.data) ? invoiceDetails.data : [];
    const gstGroups = buildGstGroups(items3mm, isInterstate);
    const showCess = hasAnyCess(items3mm);
    
    if (gstGroups.length > 0) {
      // Create table with proper spacing - different for interstate vs intrastate; CESS column only when any product has CESS
      let tableLine1: string, tableLine2: string, tableLine3: string;
      if (isInterstate) {
        if (showCess) {
          tableLine1 = `+--------+----------+----------+`;
          tableLine2 = `| GST %  |   IGST   |   CESS   |`;
          tableLine3 = `+--------+----------+----------+`;
        } else {
          tableLine1 = `+--------+----------+`;
          tableLine2 = `| GST %  |   IGST   |`;
          tableLine3 = `+--------+----------+`;
        }
      } else {
        if (showCess) {
          tableLine1 = `+--------+----------+----------+----------+`;
          tableLine2 = `| GST %  |   SGST   |   CGST   |   CESS   |`;
          tableLine3 = `+--------+----------+----------+----------+`;
        } else {
          tableLine1 = `+--------+----------+----------+`;
          tableLine2 = `| GST %  |   SGST   |   CGST   |`;
          tableLine3 = `+--------+----------+----------+`;
        }
      }
      
      const tableWidth = tableLine1.length;
      const tablePadding = Math.max(0, Math.floor((totalWidth - tableWidth) / 2));
      const tableIndent = ' '.repeat(tablePadding);
      
      addLine(tableIndent + tableLine1);
      addLine(tableIndent + tableLine2);
      addLine(tableIndent + tableLine3);
      
      for (const grp of gstGroups) {
        const gstPercentStr = grp.gstPercent.toString();
        let rowLine: string;
        if (isInterstate) {
          rowLine = showCess
            ? `|   ${gstPercentStr.padStart(2)}   |  ${disp3mm(grp.igst).toFixed(2).padStart(6)}  |  ${disp3mm(grp.cess).toFixed(2).padStart(6)}  |`
            : `|   ${gstPercentStr.padStart(2)}   |  ${disp3mm(grp.igst).toFixed(2).padStart(6)}  |`;
        } else {
          rowLine = showCess
            ? `|   ${gstPercentStr.padStart(2)}   |  ${disp3mm(grp.sgst).toFixed(2).padStart(6)}  |  ${disp3mm(grp.cgst).toFixed(2).padStart(6)}  |  ${disp3mm(grp.cess).toFixed(2).padStart(6)}  |`
            : `|   ${gstPercentStr.padStart(2)}   |  ${disp3mm(grp.sgst).toFixed(2).padStart(6)}  |  ${disp3mm(grp.cgst).toFixed(2).padStart(6)}  |`;
        }
        addLine(tableIndent + rowLine);
      }
      
      addLine(tableIndent + tableLine3);
    }
    printContent += '-'.repeat(totalWidth) + '\n';
  }
  
  // Terms and Conditions from API - show only when non-empty (no hardcoded text)
  const terms3mm = (firstItem.InvoiceTermsConditions != null && String(firstItem.InvoiceTermsConditions).trim() !== '') ? String(firstItem.InvoiceTermsConditions).trim() : '';
  if (terms3mm) {
    addLine(centerText('TERMS AND CONDITIONS'));
    terms3mm.split(/\r?\n/).forEach((line) => {
      const trimmed = line.trim();
      if (trimmed) addLine(trimmed + ' '.repeat(Math.max(0, totalWidth - trimmed.length)));
    });
    printContent += '-'.repeat(totalWidth) + '\n';
  }
  
  // Savings (display as positive for Credit Note)
  let totalDiscount = 0;
  if (Array.isArray(invoiceDetails.data)) {
    totalDiscount = invoiceDetails.data.reduce((sum, item) => sum + (Number(item.Discount || 0) + Number(item.ldiscount || 0)), 0);
  }
  // printContent += `DEBUG: GST=${isGSTRegistered ? 'YES' : 'NO'} HSN=${isGSTRegistered ? 'SHOW' : 'HIDE'} ID:${functionId} V4.2\n`;
  addLine(`You have saved Rs.${disp3mm(totalDiscount).toFixed(2)}`);
  printContent += '-'.repeat(totalWidth) + '\n';
  
  // Add extra line feeds for paper cutting
  printContent += '\n\n\n';
  
  // Add debug identifier to help track which version is being printed
  
  
  console.log(`=== FINAL 3MM PRINT CONTENT [${timestamp}] ===`);
  console.log('Content length:', printContent.length);
  console.log('GST Registration Status for this print:', isGSTRegistered ? 'GST-REGISTERED' : 'NON-GST');
  console.log('HSN Column should be:', isGSTRegistered ? 'VISIBLE' : 'HIDDEN');
  console.log('📄 Here is how your 3mm invoice will look:');
  console.log('==========================================');
  console.log(printContent);
  console.log('==========================================');
  console.log('=== END 3MM PRINT CONTENT ===');
  
  // Import unified Bluetooth interface and thermal logo printer
  const { printWithBluetooth, isBluetoothSupported } = await import('./unifiedBluetoothPrinter');
  const { Capacitor } = await import('@capacitor/core');
  const isAndroid = Capacitor.getPlatform() === 'android';
  
  // Company logo disabled on Android Bluetooth thermal printer (no header logo)
  let logoData: Uint8Array | null = null;
  const invoiceIdForLogo3mm = firstItem.Invoiceid ?? invoiceDetails.Invoiceid ?? invoiceDetails.InvoiceID;
  if (false) { // was: isAndroid && invoiceIdForLogo3mm != null - logo removed for Android
    const invoiceId = String(invoiceIdForLogo3mm);
    const logoUrl = await fetchCompanyLogo(invoiceId);
    if (logoUrl) {
      const baseUrl = getApiBaseUrl();
      const token = localStorage.getItem('token') || '';
      const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      const fullLogoUrl = logoUrl.startsWith('http') ? logoUrl : `${baseUrl.replace(/\/$/, '')}${logoUrl.startsWith('/') ? '' : '/'}${logoUrl}`;
      const { imageUrlToEscPosBitmap } = await import('./thermalLogoPrinter');
      logoData = await imageUrlToEscPosBitmap(fullLogoUrl, 384, authToken);
      if (logoData) {
        console.log('[printKohineer] Company logo converted for thermal print');
      }
    }
  }
  
  // Check if Bluetooth is supported on this platform
  const supported = await isBluetoothSupported();
  if (supported) {
    console.log('🟢 Bluetooth available, attempting to print 3mm invoice via unified interface...');
    
    try {
      // Add ESC ! 0 escape sequence to set printer to normal character mode
      const escapeSequence = '\x1B\x21\x00'; // ESC ! 0
      const printContentWithEscape = escapeSequence + printContent;
      
      const result = await printWithBluetooth(printContentWithEscape, logoData);
      
      if (result.success) {
        console.log('✅ 3mm Invoice print successful!');
        return { success: true, message: result.message || 'Invoice printed successfully' };
      } else {
        throw new Error(result.message || 'Print failed');
      }
    } catch (error: any) {
      console.error('❌ Bluetooth print failed:', error);
      throw error;
    }
  } else {
    console.log('🟡 Bluetooth not available, showing print preview only');
    console.log('Print content would be sent to printer:', printContent);
    return { success: true, message: 'Bluetooth not available - preview mode' };
  }
}


// NOTE: The following imports may cause TypeScript errors if the modules are not installed or their types are missing.
// If you are running in a context where jsPDF or jspdf-autotable are not available, you may need to install them or provide type declarations.
// If you only use this file in a browser context where these are loaded via <script>, you can ignore these imports or use dynamic imports as needed.

// @ts-ignore
import jsPDF from 'jspdf';
// @ts-ignore
import autoTable from 'jspdf-autotable';

// Helper to draw the invoice PDF content for Kohinoor 2mm/3mm
export type DrawInvoicePdfOptions = { thermalFriendly?: boolean; showSerialInPrint?: boolean; serialPrintTemplate?: string };

/** Estimate content height in mm so the PDF page can be sized without extra white space. */
export function estimateInvoicePdfHeight(invData: any, paperWidth: number): number {
  const numItems = Array.isArray(invData?.data) ? invData.data.length : 0;
  const lineHeightRow = paperWidth === 80 ? 3.5 : 3.2;
  const rowHeightApprox = 4 + lineHeightRow + 2 + 2 * lineHeightRow + 1; // one item name line + ~2 number lines
  const tableRowsMm = numItems * rowHeightApprox;
  const headerAndFooterMm = 175; // logo/company, bill header, customer, table header, totals, payment, amount in words, GST, terms, saved
  const bottomMargin = 15;
  return Math.ceil(headerAndFooterMm + tableRowsMm + bottomMargin);
}

export async function drawInvoicePdf(
  doc: any,
  invData: any,
  paperWidth: number,
  offsetX: number = 0,
  invoiceId: string | null = null,
  options?: DrawInvoicePdfOptions
) {
  // Log full invData in JSON format (like Postman)
  console.log('=== FULL invData (JSON) ===');
  console.log(JSON.stringify(invData, null, 2));
  console.log('=== END invData ===');
  console.log('[drawInvoicePdf] invData:', invData);
  console.log('[drawInvoicePdf] invData.data:', invData.data, Array.isArray(invData.data), invData.data && invData.data.length);
  console.log('[drawInvoicePdf] invData.Header:', invData.Header);
  console.log('[drawInvoicePdf] invData.data[0]?.Header:', invData.data && invData.data[0]?.Header);
  
  // Check if company is GST registered (similar to printKohineer logic)
  const firstItem = invData.data && invData.data[0] ? invData.data[0] : {};
  const isGSTRegistered = firstItem.GST === 1 || firstItem.GSTN || firstItem.GSTNo;
  console.log('GST Registration Status for this print:', isGSTRegistered ? 'GST-REGISTERED' : 'NON-GST');
  
  // Fetch company logo if invoice ID is provided
  let logoUrl = null;
  if (invoiceId) {
    try {
      logoUrl = await fetchCompanyLogo(invoiceId);
      console.log('[drawInvoicePdf] Logo URL:', logoUrl);
    } catch (error) {
      console.error('[drawInvoicePdf] Error fetching logo:', error);
    }
  }
  
  // Initialize y position
  let y = 6;
  
  // Company logo at the top (if available)
  if (logoUrl) {
    try {
      // Load and display the logo
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = logoUrl;
      });
      
      // Calculate logo dimensions to fit within paper width
      const maxLogoWidth = paperWidth - 4; // Leave 2mm margin on each side
      const maxLogoHeight = 15; // Maximum height for logo
      
      let logoWidth = img.width;
      let logoHeight = img.height;
      
      // Scale down if logo is too large
      if (logoWidth > maxLogoWidth) {
        const scale = maxLogoWidth / logoWidth;
        logoWidth = maxLogoWidth;
        logoHeight = logoHeight * scale;
      }
      
      if (logoHeight > maxLogoHeight) {
        const scale = maxLogoHeight / logoHeight;
        logoHeight = maxLogoHeight;
        logoWidth = logoWidth * scale;
      }
      
      // Center the logo horizontally
      const logoX = offsetX + (paperWidth - logoWidth) / 2;
      
      // Add logo to PDF
      doc.addImage(img, 'PNG', logoX, y, logoWidth, logoHeight);
      y += logoHeight + 3; // Add space after logo
      
      console.log('[drawInvoicePdf] Logo added successfully');
    } catch (error) {
      console.error('[drawInvoicePdf] Error loading logo image:', error);
      // Continue without logo if there's an error
    }
  }
  
  // Company details from first record (API InvoiceFullDetails)
  const companyData = invData.data && invData.data[0] ? invData.data[0] : invData;
  // Company name from API "Name" field - show on both print and preview (with or without logo)
  const companyNamePdf = (companyData.Name != null && String(companyData.Name).trim() !== '') ? String(companyData.Name).trim() : '';
  if (companyNamePdf) {
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text(companyNamePdf, offsetX + paperWidth/2, y, { align: 'center' });
    y += 6;
    doc.setFont(undefined, 'normal');
  }
  // Second line below company name (from api/InvoiceFullDetails secondline)
  const secondlinePdf = (companyData.secondline != null && String(companyData.secondline).trim() !== '') ? String(companyData.secondline).trim() : (companyData.SecondLine != null && String(companyData.SecondLine).trim() !== '') ? String(companyData.SecondLine).trim() : '';
  if (secondlinePdf) {
    doc.setFontSize(9);
    doc.text(secondlinePdf, offsetX + paperWidth/2, y, { align: 'center' });
    y += 5;
  }
  
  doc.setFontSize(9);
  
  // Company Address (try multiple field names)
  const address1 = companyData.Address1 || companyData.Address || companyData.CompanyAddress || '';
  const address2 = companyData.Address2 || '';
  const address3 = companyData.Address3 || '';
  
  if (address1 || address2 || address3) {
    const addressLines = [address1, address2, address3].filter(Boolean);
    doc.text(addressLines.join('\n'), offsetX + paperWidth/2, y, { align: 'center' });
    y += addressLines.length * 3 + 1;
  }
  
  // Company City/State/Pin
  const companyCity = companyData.City || companyData.CompanyCity || '';
  const companyState = companyData.CmpState || companyData.State || companyData.CompanyState || '';
  const companyPin = companyData.PinZip || companyData.Pin || companyData.Zip || '';
  
  if (companyCity || companyState || companyPin) {
    const cityStatePin = [companyCity, companyState, companyPin].filter(Boolean).join(', ');
    doc.text(cityStatePin, offsetX + paperWidth/2, y, { align: 'center' });
    y += 5;
  }
  
  // Company GST - only show if it's a proper GST number (not just "1")
  const companyGST = companyData.GSTN || companyData.GST || companyData.CompanyGST || '';
  if (companyGST && companyGST !== '1' && companyGST.length > 1) { 
    doc.text('GST : ' + companyGST, offsetX + paperWidth/2, y, { align: 'center' }); 
    y += 5; 
  }
  
  // Company Phone
  const companyPhone = companyData.Mobile || companyData.Phone || companyData.CompanyPhone || '';
  if (companyPhone) { 
    doc.text('Phone No: ' + companyPhone, offsetX + paperWidth/2, y, { align: 'center' }); 
    y += 5; 
  }
  
  // Company Email
  const companyEmail = companyData.Email || companyData.CompanyEmail || '';
  if (companyEmail) { 
    doc.text('Email: ' + companyEmail, offsetX + paperWidth/2, y, { align: 'center' }); 
    y += 5; 
  }
  y += 2;
  // Bill Header background with border (from API or fallback to "BILL OF SUPPLY")
  // Get Header from API response - check multiple possible locations
  // Prioritize firstItem since that's where Header usually is based on API structure
  const firstItemForHeader = invData.data && invData.data[0] ? invData.data[0] : {};
  const billHeader = firstItemForHeader.Header || 
                     firstItemForHeader.header ||
                     invData.Header || 
                     invData.header ||
                     'BILL OF SUPPLY';
  console.log('[drawInvoicePdf] billHeader value:', billHeader);
  console.log('[drawInvoicePdf] Header search - firstItemForHeader.Header:', firstItemForHeader.Header);
  console.log('[drawInvoicePdf] Header search - firstItemForHeader.header:', firstItemForHeader.header);
  console.log('[drawInvoicePdf] Header search - invData.Header:', invData.Header);
  console.log('[drawInvoicePdf] Header search - invData.header:', invData.header);
  // Display-only: for Credit Note, show all amounts as positive
  const isCreditNote = String(billHeader || '').trim() === 'Credit Note';
  const disp = (v: number) => (isCreditNote ? Math.abs(v) : v);
  // 3-inch thermal: use white fill + black border so preview and print look identical (thermal printers often render grey as solid black and hide text)
  const is3Inch = paperWidth === 80;
  const headerFillR = is3Inch ? 255 : 200;
  const headerFillG = is3Inch ? 255 : 200;
  const headerFillB = is3Inch ? 255 : 200;
  // Safe content area: 3-inch needs larger margin so print dialog and thermal printer don't clip (they often use smaller printable area than PDF)
  const contentLeft = offsetX + 2;
  const contentWidth = paperWidth === 80 ? paperWidth - 14 : paperWidth - 6; // 7mm each side for 80mm, else 3mm
  const contentRight = contentLeft + contentWidth;
  doc.setFillColor(headerFillR, headerFillG, headerFillB);
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(is3Inch ? 0.25 : 0.1);
  doc.rect(contentLeft, y, contentWidth, 7, 'FD');
  if (!is3Inch) doc.setLineWidth(0.1);
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(billHeader, contentLeft + contentWidth / 2, y + 5, { align: 'center' });
  doc.setFont(undefined, 'normal');
  y += 10;
  // Get bill date from multiple possible sources
  const billDateRaw = invData.CreatedOn || invData.BillDate || invData.CreatedDate || invData.InvoiceDate || '';
  let billDate = '';
  let billTime = '';
  if (billDateRaw) {
    const d = new Date(billDateRaw);
    billDate = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    billTime = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
  }
  
  // If still no date, try to get from first record
  if (!billDate && invData.data && invData.data[0]) {
    const firstRecord = invData.data[0];
    const recordDateRaw = firstRecord.CreatedOn || firstRecord.BillDate || firstRecord.CreatedDate || firstRecord.InvoiceDate || '';
    if (recordDateRaw) {
      const d = new Date(recordDateRaw);
      billDate = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
      billTime = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
    }
  }
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  
  // Get bill details from first record or direct fields
  const billData = invData.data && invData.data[0] ? invData.data[0] : invData;
  
  // Bill Number (use dynamic header) - InvoicePrefix from API, show only when present
  const invoicePrefixPdf = (billData.InvoicePrefix != null && String(billData.InvoicePrefix).trim() !== '') ? String(billData.InvoicePrefix).trim() : '';
  const billNumberRaw = billData.InvSeqNo || billData.BillNo || billData.InvoiceNo || invData.InvSeqNo || invData.BillNo || '';
  const billNumber = invoicePrefixPdf ? `${invoicePrefixPdf}${billNumberRaw}` : billNumberRaw;
  if (billNumber) {
    doc.text(billHeader + ' No: ' + billNumber, contentLeft, y, { maxWidth: contentWidth });
    y += 4;
  }
  
  // Bill Date (use dynamic header)
  if (billDate) {
    doc.text(billHeader + ' Date: ' + billDate, contentLeft, y, { maxWidth: contentWidth });
    y += 4;
  }
  
  // Bill Time
  if (billTime) {
    doc.text('Time: ' + billTime, contentLeft, y, { maxWidth: contentWidth });
    y += 2;
  }
  // Customer Details section
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.line(contentLeft, y, contentRight, y); // horizontal line
  y += 4;
  doc.setFontSize(9);
  doc.text('Customer Details', contentLeft + contentWidth / 2, y, { align: 'center' });
  y += 2;
  doc.setDrawColor(0);
  doc.line(contentLeft, y, contentRight, y); // horizontal line
  y += 4;
  doc.setFontSize(8);
  
  // Get customer details from first record or direct fields
  const customerData = invData.data && invData.data[0] ? invData.data[0] : invData;
  
  // Customer Name (only FName)
  const customerName = customerData.FName || 'UNKNOWN';
  doc.text(customerName, offsetX + 2, y);
  y += 4;
  
  // Customer Phone (only Mobile1)
  const customerPhone = customerData.Mobile1 || 'UNKNOWN';
  doc.text('Phone : ' + customerPhone, offsetX + 2, y);
  y += 4;
  
  // Table column positions and widths - Different for GST vs Non-GST
  let colX, colW;
  if (isGSTRegistered) {
    // GST company layout: S.No, Item Name, HSN, MRP, Qty, GST, Disc, OfferPrice, Total
    // Row 1: S.No (colX[0]), Item Name (colX[1])
    // Row 2: HSN (colX[0]), MRP (colX[1]), Qty (colX[2]), GST (colX[3]), Disc (colX[4]), OfferPrice (colX[5]), Total (colX[6])
    // Reduced gaps to fit all columns including Total
    // Columns: S.No/HSN (0), Item Name/MRP (1), Qty (2), GST (3), Disc (4), OfferPrice (5), Total (6)
    // More gap between MRP and Qty; Qty 1–2mm nearer to Offer Price
    if (paperWidth === 80) {
      colX = [2, 9, 19, 28, 35, 46];  // Qty start +1mm from MRP; Offer -1mm so Qty nearer Offer
      colW = [7, 9, 9, 9, 9, 20];
    } else {
      colX = [2, 8, 17, 26, 31, 44];  // Qty start +1mm from MRP; Offer -1mm so Qty nearer Offer
      colW = [6, 8, 8, 8, 10, 12];
    }
  } else {
    // Non-GST company layout - MRP, Qty, Disc, OfferPrice, Total
    // Row 1: S.No (colX[0]), Item Name (colX[1])
    // Row 2: MRP (colX[0]), Qty (colX[2]), Disc (colX[3]), OfferPrice (colX[4]), Total (colX[5])
    console.log('Using Non-GST layout (MRP, Qty, Disc, Offer Price, Total) with dedicated total column');
    if (paperWidth === 80) {
      colX = [2, 9, 18, 27, 36, 46];
      colW = [7, 11, 9, 9, 9, 20];
    } else {
      colX = [2, 8, 18, 28, 38, 48];
      colW = [6, 10, 8, 8, 8, 8];
    }
  }
  
  const tableRight = contentRight;
  // Total column position/width (last column of the layout)
  const totalColX = offsetX + colX[colX.length - 1];
  const totalColWidth = colW[colW.length - 1];
  // Helper: draw text in a cell with wrapping so values stay in column and wrap to next line (like Bluetooth thermal)
  const lineHeightRow = paperWidth === 80 ? 3.5 : 3.2;
  const drawCellWrapped = (text: string, x: number, y: number, colWidthMm: number, opts: { align?: 'left' | 'center' | 'right' } = {}): number => {
    const str = String(text ?? '');
    if (!str) return 1;
    const maxW = Math.max(2, colWidthMm - 0.5);
    const lines = doc.splitTextToSize(str, maxW);
    lines.forEach((line: string, i: number) => {
      doc.text(line, x, y + i * lineHeightRow, opts);
    });
    return lines.length;
  };
  // Table header background with border - 3-inch uses white + black border so print matches preview
  const headerStartY = y - 1.5;
  const headerHeight = 7.5;
  doc.setFillColor(headerFillR, headerFillG, headerFillB);
  doc.setDrawColor(is3Inch ? 0 : 180, is3Inch ? 0 : 180, is3Inch ? 0 : 180);
  if (is3Inch) doc.setLineWidth(0.25);
  doc.rect(contentLeft, headerStartY, contentWidth, headerHeight, 'FD');
  if (!is3Inch) { doc.setDrawColor(180, 180, 180); doc.setLineWidth(0.1); }
  doc.line(offsetX + colX[0], headerStartY, tableRight, headerStartY);
  
  // Header row 1: S.No, Item Name
  doc.setFont(undefined, 'bold');
  doc.setFontSize(paperWidth === 80 ? 7.5 : 6);
  doc.text('S.No', offsetX + colX[0] + 1, y + 1.5);
  doc.text('Item Name', offsetX + colX[1] + 1, y + 1.5);
  
  // isIGST from API: 0 = intrastate (SGST+CGST), 1 = interstate (IGST only). Fallback to CustState !== CmpState.
  const isIGST = firstItem.isIGST === 1 || firstItem.isIGST === true || (invData as any).isIGST === 1 || (invData as any).isIGST === true
    || !!(firstItem.CustState && firstItem.CmpState && firstItem.CustState !== firstItem.CmpState);
  const isInterstate = isIGST;
  const gstColumnHeader = isInterstate ? 'IGST' : 'GST';
  
  // Header row 2: HSN, MRP, Qty, OfferPrice, Total (GST column hidden in product table)
  if (isGSTRegistered) {
    doc.text('HSN', offsetX + colX[0] + 1, y + 4.5);
    doc.text('MRP', offsetX + colX[1] + 1, y + 4.5);
    doc.text('Qty', offsetX + colX[2] + colW[2]/2, y + 4.5, { align: 'center' });
    // GST column header intentionally not drawn (hidden in product table; GST calculation table unchanged)
    doc.text(['Offer', 'Price'], offsetX + colX[4] + colW[4]/2, y + 3.5, { align: 'center', lineHeightFactor: 1 });
    doc.text('Total', totalColX + totalColWidth/2, y + 4.5, { align: 'center' });
  } else {
    // Non-GST company headers: MRP, Qty, Disc, OfferPrice, Total
    // MRP left-aligned (starting from left, same position as S.No), others center-aligned
    // Use column widths for center alignment to prevent overlap
    doc.text('MRP', offsetX + colX[0] + 1, y + 4.5);
    doc.text('Qty', offsetX + colX[2] + colW[2]/2, y + 4.5, { align: 'center' });
    doc.text('Disc', offsetX + colX[3] + colW[3]/2, y + 4.5, { align: 'center' });
    // Stack OfferPrice to mirror GST styling
    doc.text(['Offer', 'Price'], offsetX + colX[4] + colW[4]/2, y + 3.5, { align: 'center', lineHeightFactor: 1 });
    // For Total column (last column), use its dedicated slot to avoid overlap with OfferPrice
    doc.text('Total', totalColX + totalColWidth/2, y + 4.5, { align: 'center' });
  }
  
  // Draw header horizontal line at the bottom - on top of background, spanning full width
  const headerLineY = headerStartY + headerHeight;
  doc.line(offsetX + colX[0], headerLineY, tableRight, headerLineY);
  y = headerLineY; // Update y to the bottom of the header
  doc.setFont(undefined, 'normal');
  
  // Totals accumulators
  let totalQty = 0, totalDisc = 0, totalGST = 0, totalTotal = 0, totalOfferPrice = 0;
  
  // Item Name uses full table width from "Item Name" to just before "Total"; may wrap to multiple lines when showSerialInPrint
  const itemNameStartX = colX[1];
  const itemNameWidthMm = Math.max(2, (colX[colX.length - 1] - colX[1]) - 0.5);
  const serialPrintTemplate = (options?.serialPrintTemplate ?? '').trim();
  const showSerialInPrint = options?.showSerialInPrint === true || (serialPrintTemplate.length > 0 && serialPrintTemplate.includes('<SerialNo>'));
  const effectiveSerialTemplate = serialPrintTemplate.includes('<SerialNo>') ? serialPrintTemplate : (options?.showSerialInPrint ? '<SerialNo>' : '');
  const truncateItemName = (name: string): string => {
    if (!name || doc.getTextWidth(name) <= itemNameWidthMm) return name;
    let t = name;
    while (t.length > 0 && doc.getTextWidth(t + '...') > itemNameWidthMm) t = t.slice(0, -1);
    return t + '...';
  };

  (invData.data || []).forEach((item, idx) => {
    doc.setFontSize(paperWidth === 80 ? 7 : 5.5);
    // Only show serial tag when SerialNumber has a real value (exclude null, empty, "-", "null")
    const rawSerial = item.SerialNumber;
    const serialNum = (rawSerial != null && rawSerial !== '' && String(rawSerial).trim() !== '' && String(rawSerial).trim() !== '-' && String(rawSerial).toLowerCase() !== 'null')
      ? String(rawSerial).trim() : '';
    const serialPart = showSerialInPrint && serialNum && effectiveSerialTemplate
      ? effectiveSerialTemplate.replace('<SerialNo>', serialNum)
      : '';
    const displayItemText = serialPart ? `${String(item.Title || '')} (${serialPart})` : String(item.Title || '');
    const itemNameLines = doc.splitTextToSize(displayItemText, itemNameWidthMm);

    // Use different price variables based on GST registration status
    let unitPrice, mrpPrice, offerPrice;
    const qty = parseFloat(item.Quantity ?? item.Qty ?? '0') || 0;
    let discountValue = 0;
    if (typeof item.ldiscount !== 'undefined' && item.ldiscount !== null) {
      discountValue = Number(item.ldiscount);
    } else if (typeof item.Discount !== 'undefined' && item.Discount !== null) {
      discountValue = Number(item.Discount);
    }
    
    const sgst = parseFloat(item.SGST) || 0;
    const cgst = parseFloat(item.CGST) || 0;
    const igst = parseFloat(item.IGST) || 0;
    const gstTotal = isIGST ? igst : (sgst + cgst);
    const totalVal = (typeof item.TotalCost !== 'undefined' && item.TotalCost !== null) ? Number(item.TotalCost) : 0;
    
    if (isGSTRegistered) {
      // For GST companies: MRP is the Rate/MRP field, OfferPrice is calculated from TotalCost
      mrpPrice = Number(item.Rate || item.MRP || item.unitprice || 100);
      // Calculate offer price dynamically: offerprice = TotalCost / Quantity
      offerPrice = qty > 0 ? totalVal / qty : mrpPrice;
      unitPrice = mrpPrice; // Keep for backward compatibility
    } else {
      // For non-GST companies: Use Rate for MRP column
      mrpPrice = Number(item.Rate || item.MRP || item.unitprice || 100);
      unitPrice = mrpPrice; // Same value for non-GST companies
      offerPrice = qty > 0 ? totalVal / qty : mrpPrice;
    }
    
    // Row 1: S.No, Item Name (wrap when long or when serial is shown; do not disturb row 2 column alignment)
    const useWrappedName = itemNameLines.length > 1 || (showSerialInPrint && serialNum);
    doc.text(String(idx + 1).padStart(2, '0'), offsetX + colX[0] + 1, y + 4);
    if (useWrappedName) {
      itemNameLines.forEach((line: string, i: number) => {
        doc.text(line, offsetX + itemNameStartX + 1, y + 4 + i * lineHeightRow);
      });
    } else {
      doc.text(truncateItemName(String(item.Title || '')), offsetX + itemNameStartX + 1, y + 4);
    }
    const row2StartY = useWrappedName
      ? y + 4 + itemNameLines.length * lineHeightRow + 2
      : y + 4 + lineHeightRow + 2;

    // Row 2: get wrapped lines per cell, then draw line-by-line so columns align (no overlapping)
    const maxW = (w: number) => Math.max(2, w - 0.5);
    let linesGst: string[][];
    let linesNonGst: string[][];
    if (isGSTRegistered) {
      const hsn = (item.HSNCode || item.HSN || '').toString().trim();
      linesGst = [
        doc.splitTextToSize(hsn, maxW(colW[0])),
        doc.splitTextToSize(disp(mrpPrice).toFixed(2), maxW(colW[1])),
        doc.splitTextToSize(disp(qty).toFixed(2), maxW(colW[2])),
        doc.splitTextToSize(disp(gstTotal).toFixed(2), maxW(colW[3])),
        doc.splitTextToSize(disp(offerPrice).toFixed(2), maxW(colW[4])),
        doc.splitTextToSize(disp(totalVal).toFixed(2), maxW(totalColWidth)),
      ];
    } else {
      linesNonGst = [
        doc.splitTextToSize(disp(mrpPrice).toFixed(2), maxW(colW[0])),
        doc.splitTextToSize(disp(qty).toFixed(2), maxW(colW[2])),
        doc.splitTextToSize(disp(discountValue).toFixed(2), maxW(colW[3])),
        doc.splitTextToSize(disp(offerPrice).toFixed(2), maxW(colW[4])),
        doc.splitTextToSize(disp(totalVal).toFixed(2), maxW(totalColWidth)),
      ];
    }
    const row2Cols = isGSTRegistered
      ? [
          { lines: linesGst![0], x: offsetX + colX[0] + 1, w: colW[0], align: undefined as 'left' | 'center' | undefined },
          { lines: linesGst![1], x: offsetX + colX[1] + 1, w: colW[1], align: undefined },
          { lines: linesGst![2], x: offsetX + colX[2] + colW[2]/2, w: colW[2], align: 'center' as const },
          { lines: linesGst![3], x: offsetX + colX[3] + colW[3]/2, w: colW[3], align: 'center' as const }, // not drawn - GST column hidden
          { lines: linesGst![4], x: offsetX + colX[4] + colW[4]/2, w: colW[4], align: 'center' as const },
          { lines: linesGst![5], x: totalColX + totalColWidth/2, w: totalColWidth, align: 'center' as const },
        ]
      : [
          { lines: linesNonGst![0], x: offsetX + colX[0] + 1, w: colW[0], align: undefined },
          { lines: linesNonGst![1], x: offsetX + colX[2] + colW[2]/2, w: colW[2], align: 'center' as const },
          { lines: linesNonGst![2], x: offsetX + colX[3] + colW[3]/2, w: colW[3], align: 'center' as const },
          { lines: linesNonGst![3], x: offsetX + colX[4] + colW[4]/2, w: colW[4], align: 'center' as const },
          { lines: linesNonGst![4], x: totalColX + totalColWidth/2, w: totalColWidth, align: 'center' as const },
        ];
    const row2Lines = Math.max(1, ...row2Cols.map(c => c.lines.length));
    for (let i = 0; i < row2Lines; i++) {
      const lineY = row2StartY + i * lineHeightRow;
      row2Cols.forEach((col, ci) => {
        if (isGSTRegistered && ci === 3) return; // GST column hidden in product table
        const line = col.lines[i] ?? '';
        if (line) doc.text(line, col.x, lineY, col.align ? { align: col.align } : {});
      });
    }
    const row1Height = useWrappedName ? 4 + itemNameLines.length * lineHeightRow + 2 : 4 + lineHeightRow + 2;
    const rowHeight = row1Height + row2Lines * lineHeightRow;
    const rowLineY = y + rowHeight + 0.5;
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.1);
    doc.line(offsetX + colX[0], rowLineY, tableRight, rowLineY);
    y += rowHeight + 1;
    
    // Accumulate totals
    totalQty += qty;
    totalDisc += discountValue;
    totalGST += gstTotal;
    totalTotal += totalVal;
    totalOfferPrice += (offerPrice * qty); // Sum of (offerPrice * qty) for totals
  });
  
  // Totals row: use min width so numbers (e.g. 40534.00) stay on one line; avoid wrapping
  const totMaxW = (w: number, minMm: number = 2) => Math.max(minMm, w - 0.5);
  const totNumMinMm = 14; // wide enough for "40534.00" on one line
  const totQtyLines = doc.splitTextToSize(disp(totalQty).toFixed(2), totMaxW(colW[2]));
  const totGstLines = doc.splitTextToSize(disp(totalGST).toFixed(2), totMaxW(colW[3]));
  const totOfferLines = doc.splitTextToSize(disp(totalOfferPrice).toFixed(2), totMaxW(colW[4], totNumMinMm));
  const totTotalLines = doc.splitTextToSize(Math.round(disp(totalTotal)).toFixed(2), totMaxW(totalColWidth, totNumMinMm));
  const totDiscLines = doc.splitTextToSize(disp(totalDisc).toFixed(2), totMaxW(colW[3]));
  const totalTotalsLines = isGSTRegistered
    ? Math.max(1, totQtyLines.length, totOfferLines.length, totTotalLines.length) // GST column hidden in product table
    : Math.max(1, totQtyLines.length, totDiscLines.length, totOfferLines.length, totTotalLines.length);
  const totalStripPaddingTop = 2;
  const totalStripPaddingBottom = 2;
  const totalStripHeight = totalStripPaddingTop + totalTotalsLines * lineHeightRow + totalStripPaddingBottom;

  doc.setFont(undefined, 'bold');
  const totalStripY = y;
  doc.setFillColor(headerFillR, headerFillG, headerFillB);
  doc.setDrawColor(is3Inch ? 0 : 180, is3Inch ? 0 : 180, is3Inch ? 0 : 180);
  if (is3Inch) doc.setLineWidth(0.25);
  doc.rect(contentLeft, totalStripY, contentWidth, totalStripHeight, 'FD');
  if (!is3Inch) doc.setLineWidth(0.1);

  const totalContentStartY = totalStripY + totalStripPaddingTop + lineHeightRow * 0.4;
  if (isGSTRegistered) {
    doc.text('Total:', offsetX + colX[0] + 1, totalContentStartY);
    for (let i = 0; i < totalTotalsLines; i++) {
      const lineY = totalContentStartY + i * lineHeightRow;
      const q = totQtyLines[i] ?? '';
      const o = totOfferLines[i] ?? '';
      const t = totTotalLines[i] ?? '';
      if (q) doc.text(q, offsetX + colX[2] + colW[2]/2, lineY, { align: 'center' });
      // GST total column hidden in product table
      if (o) doc.text(o, offsetX + colX[4] + colW[4]/2, lineY, { align: 'center' });
      if (t) doc.text(t, totalColX + totalColWidth/2, lineY, { align: 'center' });
    }
  } else {
    doc.text('Total:', offsetX + colX[0] + 1, totalContentStartY);
    for (let i = 0; i < totalTotalsLines; i++) {
      const lineY = totalContentStartY + i * lineHeightRow;
      const q = totQtyLines[i] ?? '';
      const d = totDiscLines[i] ?? '';
      const o = totOfferLines[i] ?? '';
      const t = totTotalLines[i] ?? '';
      if (q) doc.text(q, offsetX + colX[2] + colW[2]/2, lineY, { align: 'center' });
      if (d) doc.text(d, offsetX + colX[3] + colW[3]/2, lineY, { align: 'center' });
      if (o) doc.text(o, offsetX + colX[4] + colW[4]/2, lineY, { align: 'center' });
      if (t) doc.text(t, totalColX + totalColWidth/2, lineY, { align: 'center' });
    }
  }

  doc.setFont(undefined, 'normal');
  y = totalStripY + totalStripHeight + 2;
  y += 4; // one-line gap between Total section border and "Received by" section

  // Received by (dynamic payment methods) - use new Payments JSON array
  doc.setFontSize(8);
  
  // Get payments from the first item in data array or from invData directly
  const paymentData = invData.data && invData.data[0] ? invData.data[0] : invData;
  const payments = parsePayments(paymentData.Payments);
  
  if (payments.length > 0) {
    payments.forEach(payment => {
      const paymentLabel = payment.PaymentMode.charAt(0).toUpperCase() + payment.PaymentMode.slice(1).toLowerCase();
      doc.text(`Received by ${paymentLabel}: Rs.${Math.round(disp(payment.amount)).toFixed(2)}`, offsetX + 2, y);
      y += 4;
    });
  } else {
    // Fallback to old payment fields if Payments JSON is not available
    const paymentMethods = [
      { label: 'Cash', value: paymentData.PaidbyCash },
      { label: 'UPI/Wallet', value: paymentData.walletamount },
      { label: 'Card', value: paymentData.PaidbyCard },
      { label: 'Coupon', value: paymentData.Couponamount },
      { label: 'Credit Note', value: paymentData.CreditNoteamount },
      { label: 'Cheque', value: paymentData.Chequeamount },
      { label: 'Others', value: paymentData.PaidbyOthers },
      { label: 'Points', value: paymentData.PaidbyPoints }
    ];
    paymentMethods.forEach(pm => {
      const val = Number(pm.value);
      if (pm.value != null && Math.abs(val) > 0) {
        doc.text(`Received by ${pm.label}: Rs.${disp(val).toFixed(2)}`, offsetX + 2, y);
        y += 4;
      }
    });
  }

  // Show Due Amount if there is any
  const dueAmountVal = Number(paymentData.DueAmount ?? invData.DueAmount);
  if (Math.abs(dueAmountVal) > 0) {
    doc.text(`Due Amount: Rs.${disp(dueAmountVal).toFixed(2)}`, offsetX + 2, y);
    y += 4;
  }

  // Add more space before amount in words for readability
  y += 4;

  // Amount in words (use InvoicedAmount or sum of TotalCost) - display as positive for Credit Note
  doc.setFontSize(7);
  let totalAmount = 0;
  if (typeof invData.InvoicedAmount !== 'undefined') {
    totalAmount = Number(invData.InvoicedAmount);
  } else if (Array.isArray(invData.data)) {
    totalAmount = invData.data.reduce((sum, item) => sum + (Number(item.TotalCost) || 0), 0);
  }
  const amountInWords = numberToWords(Math.round(disp(totalAmount))).toUpperCase();
  
  // Add title with more spacing
  doc.text('TOTAL TAX INVOICE AMOUNT IN WORDS', offsetX + 2, y);
  y += 5;
  
  // Wrap amount in words if too long with better spacing
  const wordsLines = doc.splitTextToSize(amountInWords, paperWidth - 8);
  wordsLines.forEach(line => {
    doc.text(line, offsetX + 2, y);
    y += 3; // Increased line spacing
  });
  
  // Add extra space after amount in words
  y += 3;
  // GST Calculation Table - Group by GST %, dynamic CESS column - Only for GST companies
  if (isGSTRegistered && Array.isArray(invData.data) && invData.data.length > 0) {
    const isInterstate = firstItem.CustState && firstItem.CmpState && firstItem.CustState !== firstItem.CmpState;
    const gstGroups = buildGstGroups(invData.data, isInterstate);
    const showCess = hasAnyCess(invData.data);
    
    if (gstGroups.length > 0) {
      // Title centered above the GST table
      doc.setFont(undefined, 'bold');
      doc.text('GST CALCULATION', contentLeft + contentWidth / 2, y, { align: 'center' });
      y += 5;
      doc.setFont(undefined, 'normal');
      
      const totalTableWidth = contentWidth;
      const colCount = (isInterstate ? 2 : 3) + (showCess ? 1 : 0);
      const colWidth = totalTableWidth / colCount;
      const colWidths = [colWidth, colWidth, colWidth, colWidth].slice(0, colCount);
      const rowHeight = 6;
      const tableX = contentLeft;
      const tableY = y;
      const totalRows = 1 + gstGroups.length; // header + data rows
      const tableHeight = rowHeight * totalRows;
      
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(paperWidth === 80 ? 0.25 : 0.15);
      doc.rect(tableX, tableY, totalTableWidth, tableHeight);
      let xAcc = tableX;
      for (let c = 0; c < colCount - 1; c++) {
        xAcc += colWidths[c];
        doc.line(xAcc, tableY, xAcc, tableY + tableHeight);
      }
      for (let r = 1; r < totalRows; r++) {
        doc.line(tableX, tableY + rowHeight * r, tableX + totalTableWidth, tableY + rowHeight * r);
      }
      
      // Header row
      doc.setFont(undefined, 'bold');
      doc.text('GST %', tableX + colWidths[0]/2, tableY + 4, { align: 'center' });
      xAcc = tableX + colWidths[0];
      doc.text(isInterstate ? 'IGST' : 'SGST', xAcc + colWidths[1]/2, tableY + 4, { align: 'center' });
      xAcc += colWidths[1];
      if (!isInterstate) {
        doc.text('CGST', xAcc + colWidths[2]/2, tableY + 4, { align: 'center' });
        xAcc += colWidths[2];
      }
      if (showCess) {
        doc.text('CESS', xAcc + colWidths[colCount - 1]/2, tableY + 4, { align: 'center' });
      }
      
      // Data rows - one per GST group
      doc.setFont(undefined, 'normal');
      gstGroups.forEach((grp, idx) => {
        const rowY = tableY + rowHeight * (idx + 1) + 4;
        doc.text(grp.gstPercent.toFixed(0), tableX + colWidths[0]/2, rowY, { align: 'center' });
        xAcc = tableX + colWidths[0];
        doc.text(disp(isInterstate ? grp.igst : grp.sgst).toFixed(2), xAcc + colWidths[1]/2, rowY, { align: 'center' });
        xAcc += colWidths[1];
        if (!isInterstate) {
          doc.text(disp(grp.cgst).toFixed(2), xAcc + colWidths[2]/2, rowY, { align: 'center' });
          xAcc += colWidths[2];
        }
        if (showCess) {
          doc.text(disp(grp.cess).toFixed(2), xAcc + colWidths[colCount - 1]/2, rowY, { align: 'center' });
        }
      });
      
      y += tableHeight + 2;
    }
  }

  // TERMS AND CONDITIONS from API - show only when non-empty (no hardcoded text)
  const termsPdf = (billData.InvoiceTermsConditions != null && String(billData.InvoiceTermsConditions).trim() !== '') ? String(billData.InvoiceTermsConditions).trim() : '';
  if (termsPdf) {
    doc.setFontSize(6.5);
    doc.text('TERMS AND CONDITIONS', 2, y);
    y += 3;
    const termsLinesPdf = termsPdf.split(/\r?\n/);
    const termsMaxW = paperWidth - 4;
    termsLinesPdf.forEach((line) => {
      const trimmed = line.trim();
      if (trimmed) {
        const wrapped = doc.splitTextToSize(trimmed, termsMaxW);
        doc.text(wrapped, 2, y);
        y += wrapped.length * 3;
      }
    });
    y += 4;
  }

  // You have saved Rs : (total discount) - always sum from line items (display as positive for Credit Note)
  doc.setFontSize(8);
  let totalDiscount = 0;
  if (Array.isArray(invData.data)) {
    totalDiscount = invData.data.reduce((sum, item) => sum + (Number(item.Discount || 0) + Number(item.ldiscount || 0)), 0);
  }
  doc.setFont(undefined, 'bold');
  const savedText = 'You have saved Rs.' + disp(totalDiscount).toFixed(2);
  doc.text(savedText, paperWidth/2, y, { align: 'center' });
  doc.setFont(undefined, 'normal');
  y += 4;
} 